import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { User } from '@shared/interfaces/index';
import { UserService } from '@shared/services/index';
import { ROUTES_APP } from '../../../../app.routes';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthenticationService } from '../../../services/authentication.service';
import { SessionService } from '../../../../core/session/session.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

enum MenuActions {
  MI_PERFIL = 'profile',
  MIS_PACIENTES = 'patients',
  CERRAR_SESION = 'closeSession',
}

enum MenuLabels {
  MI_PERFIL = 'Mi perfil',
  MIS_PACIENTES = 'Mis pacientes',
  CERRAR_SESION = 'Cerrar sesión',
}

type MenuItem = {
  label: string;
  icon: string;
  iconActive: string;
  id: string;
  show: boolean;
  action: () => void;
};

@Component({
  selector: 'soph-header-sophia',
  standalone: true,
  imports: [MatProgressSpinnerModule],
  templateUrl: './header-sophia.component.html',
  styleUrl: './header-sophia.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderSophiaComponent implements OnInit, OnDestroy {
  private readonly iconRegistry: MatIconRegistry = inject(MatIconRegistry);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

  private readonly session = inject(SessionService);
  protected readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  private subscriptions: Subscription[] = [];

  userInfo: User | undefined;
  userName: WritableSignal<string | undefined> = signal(undefined);
  activeMenuItem: WritableSignal<MenuActions | null> = signal(null);
  protected readonly menuItems: MenuItem[] = [
    {
      id: MenuActions.MI_PERFIL,
      label: MenuLabels.MI_PERFIL,
      icon: 'Persona_outline',
      iconActive: 'profile',
      show: true,
      action: () => {},
    },
    {
      id: MenuActions.MIS_PACIENTES,
      label: MenuLabels.MIS_PACIENTES,
      icon: 'especialidad',
      iconActive: 'especialidad_contrast',
      show: this.authService.isSelectedMedicalCenter(),
      action: () => this.router.navigate([ROUTES_APP.MIS_PACIENTES]),
    },
    {
      id: MenuActions.CERRAR_SESION,
      label: MenuLabels.CERRAR_SESION,
      icon: 'logout',
      iconActive: 'logout_contrast',
      show: true,
      action: () => this.session.logout(),
    },
  ];

  async ngOnInit(): Promise<void> {
    this.registerIcons();
    await this.prepareUserInfo();
  }

  async prepareUserInfo(): Promise<void> {
    await this.userService.init();
    const subscription = this.userService.user.subscribe((value: User | undefined) => {
      this.userInfo = value;
      const { medicos, asistentes } = value || {};
      const fullName: string | undefined = medicos?.nombreCompleto || asistentes?.nombreCompleto;
      if (fullName) {
        const firstName: string = fullName.split(' ')[0];
        this.userName.set(firstName);
      }
    });
    this.subscriptions.push(subscription);
  }

  registerIcons() {
    this.iconRegistry.addSvgIcon(
      'custom-eye-open',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/eye_outline_table.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'calendar-info-consulta',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/calendar_info_consulta_table.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'hospital-table',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/hospital_table.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'estado-consulta',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/estado_consulta.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'avatar-contrast',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/profile.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'badge',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/badge.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'cancel-contrast',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/cancel_contrast.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'health-contrast',
      this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/Health-1-contrast.svg`)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  iconToShow(id: string): string {
    const itemObject: MenuItem | undefined = this.menuItems.find((item) => item.id === id);
    if (!itemObject) return `assets/icons/eye.svg`;

    const { icon, iconActive } = itemObject;
    return `assets/icons/${this.activeMenuItem() === id ? iconActive : icon}.svg`;
  }

  mouseInteraction({ type }: MouseEvent, origen: string): void {
    this.activeMenuItem.set(type === 'mouseenter' ? (origen as MenuActions) : null);
  }
}
